/**
 * ShopProfilePanel - Shop configuration settings
 * Handles: Business name, address, timezone, logo, saved driver cost
 */
import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Image as ImageIcon, Upload, Trash2, Loader2 } from 'lucide-react';
import { API } from '@/config/constants';
import { useShopProfile } from '@/hooks/useShopProfile';
import { useShopSettings } from '@/context/ShopSettingsContext';
import AddressAutocomplete from '@/components/AddressAutocomplete';
import DriverDeliveryCostField from './DriverDeliveryCostField';
import { formatCurrency } from '@/utils/orderValue';

// US Timezones from IANA database
const US_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)', region: 'New York, Miami, Atlanta' },
  { value: 'America/Chicago', label: 'Central Time (CT)', region: 'Chicago, Dallas, Houston' },
  { value: 'America/Denver', label: 'Mountain Time (MT)', region: 'Denver, Salt Lake City' },
  { value: 'America/Phoenix', label: 'Mountain Time - Arizona (no DST)', region: 'Phoenix, Tucson' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)', region: 'Los Angeles, San Francisco, Seattle' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)', region: 'Anchorage, Juneau' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)', region: 'Honolulu (no DST)' },
  { value: 'America/Puerto_Rico', label: 'Atlantic Time (AT)', region: 'Puerto Rico, US Virgin Islands' },
];

const ShopProfilePanel = ({ canManage = false }) => {
  const { refreshBranding } = useShopSettings() || {};
  const profile = useShopProfile({ canManage, refreshBranding });
  const {
    settings, draft: editForm, setDraft: setEditForm, editing, loading,
    loadError, busy, operation, status, message, readOnly, draftReadOnly, canMutate, selectedFile, uncertain = true,
    deliveryCommissionAvailable = true,
  } = profile;
  const logoInputRef = useRef(null);
  const [unavailableLogo, setUnavailableLogo] = useState(null);
  const logoUrl = settings?.logo_id
    ? `${API}/public/shop-logo?v=${encodeURIComponent(settings.logo_id)}` : null;
  const logoUploading = busy && operation === 'upload';
  const saving = busy && operation === 'save';

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    profile.uploadLogo(file);
  };

  const handleLogoDelete = () => {
    if (!canMutate) return;
    if (!window.confirm('Remove the shop logo?')) return;
    profile.removeLogo();
  };

  const handleAddressSelect = (parsed) => {
    setEditForm(f => ({
      ...f,
      street_address: parsed.street || f.street_address,
      city: parsed.city || f.city,
      state: parsed.state || f.state,
      zip_code: parsed.zip || f.zip_code,
    }));
  };

  if (!settings) {
    return <div className="space-y-2" role="status">
      <p className="text-sm text-stone-500">{loading ? 'Loading shop profile...' : loadError || message || 'Shop profile is unavailable.'}</p>
      {!loading && status !== 'retired' && <Button variant="outline" size="sm" onClick={profile.retryLoading} disabled={busy}>Retry loading</Button>}
    </div>;
  }

  const selectedTimezone = US_TIMEZONES.find(tz => tz.value === settings.timezone);

  // Build a display-friendly address for view mode
  const cityLine = [
    settings.city,
    [settings.state, settings.zip_code].filter(Boolean).join(' '),
  ].filter(Boolean).join(', ');
  const addressLines = settings.street_address
    ? [
        [settings.street_address, settings.street_address_2].filter(Boolean).join(', '),
        cityLine,
      ].filter(Boolean)
    : settings.address
    ? [settings.address]
    : [];

  return (
    <div className="space-y-6">
      {(message || busy) && <div role="status" className="rounded-md border border-stone-200 bg-stone-50 p-3 space-y-2 text-sm" data-testid="shop-profile-status">
        <p>{busy ? `${operation === 'save' ? 'Saving profile' : operation === 'upload' ? 'Uploading logo' : operation === 'delete' ? 'Removing logo' : operation === 'location' ? 'Updating shop map location' : 'Checking saved settings'}...` : message}</p>
        {!busy && canManage && <div className="flex flex-wrap gap-2">
          {['unknown', 'rejected'].includes(status) && <Button variant="outline" size="sm" onClick={profile.checkSavedSettings} disabled={readOnly}>Check saved settings</Button>}
          {status === 'checked' && <>
            {uncertain && <p className="w-full">These are the current saved settings, not proof that the earlier request has stopped. Retrying may repeat a change.</p>}
            <dl className="w-full text-sm" data-testid="shop-profile-readback">
              <dt className="font-medium">Current saved profile</dt>
              <dd>{settings.name || 'Name not set'}</dd>
              <dd>{addressLines.join(', ') || 'Address not set'}</dd>
              <dd>{settings.timezone || 'Timezone not set'}</dd>
              <dd>Driver cost per delivery: {!deliveryCommissionAvailable ? 'Unavailable offline'
                : settings.deliveryCommission === undefined ? 'Not set' : formatCurrency(settings.deliveryCommission)}</dd>
              <dd>{settings.logo_id ? 'Saved logo shown below' : 'No saved logo'}</dd>
            </dl>
            <Button variant="outline" size="sm" onClick={profile.acceptSavedSettings} disabled={readOnly}>Use current saved settings</Button>
            <Button variant="outline" size="sm" onClick={() => {
              if (!uncertain || window.confirm('The earlier request may still complete. Retry this change anyway?')) profile.retryMutation();
            }} disabled={readOnly}>{uncertain ? 'Retry change anyway' : 'Retry change'}</Button>
          </>}
          {status === 'rejected' && <Button variant="outline" size="sm" onClick={profile.retryMutation} disabled={readOnly}>Retry change</Button>}
          {status === 'refresh-required' && <Button variant="outline" size="sm" onClick={profile.retryRefresh} disabled={readOnly}>Retry refresh only</Button>}
        </div>}
      </div>}
      {selectedFile && <div className="text-sm space-y-2" data-testid="shop-logo-selection">
        <p>Selected logo: {selectedFile.name}. Kept only while this profile stays open.</p>
        {!busy && ['rejected', 'checked'].includes(status) && <Button variant="outline" size="sm" disabled={readOnly} onClick={profile.discardSelection}>Discard selected logo</Button>}
      </div>}
      <div className="space-y-2 text-sm" data-testid="shop-map-location">
        <p>{settings.route_origin?.state === 'ready' ? 'Shop map location established.'
          : settings.route_origin?.address ? 'Shop map location needs an administrator update. Online routes can use the saved address; offline routes need an established point.'
          : 'Save a shop address to enable delivery route starts.'}</p>
        {settings.route_origin?.address && <p>Saved route address: {settings.route_origin.address}</p>}
        {canManage && <Button variant="outline" size="sm" onClick={profile.updateMapLocation}
          disabled={!canMutate || !settings.route_origin?.address || !settings.location_baseline}>
          Update shop map location
        </Button>}
        {canManage && <p className="text-xs text-stone-500">Uses the current saved address. Unsaved profile edits are kept.</p>}
      </div>
      {/* Logo — always visible, independent of view/edit mode */}
      <div className="flex items-start gap-4" data-testid="shop-logo-section">
        <div className="w-24 h-24 rounded-lg border border-stone-200 bg-stone-50 flex items-center justify-center overflow-hidden flex-shrink-0">
          {logoUrl && unavailableLogo !== logoUrl ? (
            <img
              key={logoUrl}
              src={logoUrl}
              alt="Shop logo"
              onError={() => setUnavailableLogo(logoUrl)}
              className="w-full h-full object-contain"
              data-testid="shop-logo-preview"
            />
          ) : logoUrl ? (
            <span className="text-xs text-center text-stone-500 p-2">Logo unavailable</span>
          ) : (
            <ImageIcon className="h-8 w-8 text-stone-300" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <Label className="text-sm font-medium text-stone-700">Shop Logo</Label>
          <p className="text-xs text-stone-400 mt-1 mb-2">
            Appears on printed enclosure cards. PNG/JPEG/WebP/SVG, up to 5 MB.
          </p>
          {canManage && <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => logoInputRef.current?.click()}
              disabled={!canMutate}
              data-testid="shop-logo-upload-btn"
              className="h-8 text-xs"
            >
              {logoUploading ? (
                <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Uploading…</>
              ) : (
                <><Upload className="h-3.5 w-3.5 mr-1" /> {logoUrl ? 'Replace' : 'Upload'}</>
              )}
            </Button>
            {logoUrl && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogoDelete}
                disabled={!canMutate}
                className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50"
                data-testid="shop-logo-delete-btn"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1" />
                Remove
              </Button>
            )}
          </div>}
          {canManage && <input
            ref={logoInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml"
            className="hidden"
            onChange={handleLogoUpload}
            disabled={!canMutate}
            aria-label="Select shop logo"
          />}
          {logoUrl && unavailableLogo === logoUrl && <Button variant="outline" size="sm" onClick={() => setUnavailableLogo(null)}>Retry logo image</Button>}
        </div>
      </div>

      {!canManage || !editing ? (
        // View mode
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <span className="text-sm font-medium text-stone-500">Shop Name</span>
              <p className="text-stone-900 mt-1">{settings.name || 'Not set'}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-stone-500">Address</span>
              {addressLines.length > 0 ? (
                <div className="mt-1">
                  {addressLines.map((line, i) => (
                    <p key={i} className={i === 0 ? 'text-stone-900' : 'text-stone-600 text-sm'}>{line}</p>
                  ))}
                </div>
              ) : (
                <p className="text-stone-900 mt-1">Not set</p>
              )}
            </div>
            <div>
              <span className="text-sm font-medium text-stone-500">Timezone</span>
              <p className="text-stone-900 mt-1">
                {selectedTimezone ? selectedTimezone.label : settings.timezone}
              </p>
              {selectedTimezone && (
                <p className="text-xs text-stone-400">{selectedTimezone.region}</p>
              )}
            </div>
            <div>
              <span className="text-sm font-medium text-stone-500">Tenant ID</span>
              <p className="text-stone-900 font-mono text-sm mt-1">lvb-default-tenant</p>
            </div>
          </div>
          {canManage && <Button
            variant="outline"
            size="sm"
            onClick={profile.startEditing}
            disabled={!canMutate}
            data-testid="edit-shop-profile"
          >
            Edit Profile
          </Button>}
        </>
      ) : (
        // Edit mode
        <>
          <fieldset disabled={readOnly || draftReadOnly || busy} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="shop-name">Shop Name</Label>
              <Input
                id="shop-name"
                value={editForm.name || ''}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                placeholder="Your shop name"
                data-testid="shop-name-input"
              />
            </div>

            {/* Structured address fields */}
            <div className="space-y-3">
              <Label>Address</Label>
              <div className="space-y-2">
                {!settings.street_address && settings.address && <div className="space-y-1">
                  <Label htmlFor="shop-flat-address">Saved address</Label>
                  <Input id="shop-flat-address" value={editForm.address || ''}
                    onChange={event => {
                      const address = event.target.value;
                      setEditForm(form => ({ ...form, address }));
                    }} />
                  <p className="text-xs text-stone-500">Edit this address, or enter a complete structured address below.</p>
                </div>}
                <AddressAutocomplete
                  disabled={readOnly || draftReadOnly || busy}
                  value={editForm.street_address || ''}
                  onChange={(val) => setEditForm(f => ({ ...f, street_address: val }))}
                  onAddressSelect={handleAddressSelect}
                  placeholder="Street address"
                />
                <Input
                  value={editForm.street_address_2 || ''}
                  onChange={(e) => setEditForm(f => ({ ...f, street_address_2: e.target.value }))}
                  placeholder="Suite, unit, floor (optional)"
                  data-testid="shop-street-address-2-input"
                />
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    value={editForm.city || ''}
                    onChange={(e) => setEditForm(f => ({ ...f, city: e.target.value }))}
                    placeholder="City"
                    className="col-span-1"
                    data-testid="shop-city-input"
                  />
                  <Input
                    value={editForm.state || ''}
                    onChange={(e) => setEditForm(f => ({ ...f, state: e.target.value.toUpperCase().slice(0, 2) }))}
                    placeholder="State"
                    className="col-span-1 uppercase"
                    maxLength={2}
                    data-testid="shop-state-input"
                  />
                  <Input
                    value={editForm.zip_code || ''}
                    onChange={(e) => setEditForm(f => ({ ...f, zip_code: e.target.value }))}
                    placeholder="ZIP"
                    className="col-span-1"
                    data-testid="shop-zip-input"
                  />
                </div>
              </div>
              <p className="text-xs text-stone-400">
                Used as the starting point for delivery route calculations.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="shop-timezone">Timezone</Label>
              <select
                id="shop-timezone"
                value={editForm.timezone || 'America/Los_Angeles'}
                onChange={(e) => setEditForm({ ...editForm, timezone: e.target.value })}
                className="flex h-10 w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-stone-400"
                data-testid="shop-timezone-select"
              >
                {US_TIMEZONES.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label} - {tz.region}
                  </option>
                ))}
              </select>
              <p className="text-xs text-stone-400">
                Saves the shop timezone. Some screens use the app's build-configured timezone; this does not immediately change dates throughout the app.
              </p>
            </div>
          </fieldset>
          <div className="flex gap-2 pt-2">
            <Button
              size="sm"
              onClick={profile.save}
              disabled={!canMutate}
              data-testid="save-shop-profile"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={profile.cancelEditing}
              disabled={busy || !['idle', 'rejected'].includes(status)}
            >
              Cancel
            </Button>
          </div>
        </>
      )}
      <DriverDeliveryCostField value={editForm.deliveryCommission || ''} savedValue={settings.deliveryCommission}
        editing={canManage && editing} available={deliveryCommissionAvailable}
        disabled={readOnly || draftReadOnly || busy}
        onChange={deliveryCommission => setEditForm(form => ({ ...form, deliveryCommission }))} />
    </div>
  );
};

export default ShopProfilePanel;
